import { Accordion, Drawer, Icon, Icons } from '@silverfort/sf-components';
import { JIT_MFA_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import JitPolicy from './JitPolicy';
import { useCallback, useState } from 'react';
import { DEFAULT_JIT } from '../consts';
import { JitPolicyModel } from '../types/JitTypes';
import { httpPost } from '../../../components/utils';
import { CreateBulkPolicyProps, handlePolicyCreationResponse, VerificationPopup } from './CreateBulkPolicy';
import { BulkAction, BulkPolicyCreationResponse } from '../types/PrivilegedAccountsTypes';

const BulkJit = ({ accountGuids, open, onClose, onSave, onError, shouldVerifyAction }: CreateBulkPolicyProps) => {
    const [editedPolicy, setEditedPolicy] = useState<JitPolicyModel>(DEFAULT_JIT);
    const [shouldShowVerificationPopup, setShouldShowVerificationPopup] = useState<boolean>(false);

    const isValidPolicy = useCallback(() => {
        return (
            editedPolicy?.mfaType === JIT_MFA_TYPES.NONE.value ||
            (editedPolicy?.mfaType === JIT_MFA_TYPES.MFA.value && editedPolicy?.tokens.length > 0) ||
            (editedPolicy?.mfaType === JIT_MFA_TYPES.BRIDGE.value &&
                editedPolicy?.vendorId !== null &&
                editedPolicy?.appId !== null)
        );
    }, [editedPolicy, editedPolicy]);

    const createBulkJitPolicy = useCallback(async () => {
        try {
            const createPolicyResponse: BulkPolicyCreationResponse = await httpPost('/jit/', {
                policy: { ...editedPolicy },
                userGuids: accountGuids
            });
            handlePolicyCreationResponse(createPolicyResponse, 'JIT', accountGuids.length, onError, onSave);
        } catch (error) {
            onError('An error occurred while creating bulk JIT policy.');
        }
        setShouldShowVerificationPopup(false);
    }, [editedPolicy, accountGuids]);

    return (
        <>
            {shouldVerifyAction && shouldShowVerificationPopup && (
                <VerificationPopup
                    action={BulkAction.JIT}
                    accountGuidsCount={accountGuids.length}
                    onApprove={() => {
                        setShouldShowVerificationPopup(false);
                        createBulkJitPolicy();
                    }}
                    onCancel={() => {
                        setShouldShowVerificationPopup(false);
                        onClose();
                    }}
                />
            )}
            <div>
                <Drawer
                    className="max-w-[720px] w-[720px]"
                    open={open}
                    headerTitle={`Bulk Protection (${accountGuids.length})`}
                    onClose={(e, reason) => reason !== 'backdropClick' && onClose()}
                    onAction={() => {
                        if (shouldVerifyAction) setShouldShowVerificationPopup(true);
                        else createBulkJitPolicy();
                    }}
                    footerActionProps={{ disabled: !isValidPolicy() }}
                    footerActionTitle="Save"
                    footerCloseTitle="Cancel">
                    <div className="text-13 w-[720px] h-full flex flex-col gap-4">
                        <Accordion
                            sx={{
                                '&:before': {
                                    display: 'none'
                                }
                            }}
                            disableGutters
                            elevation={0}
                            defaultExpanded={true}
                            className="shadow-none border rounded-lg !border-sf-gray-100"
                            summary={
                                <div className="h-[32px] items-center flex pr-2 font-medium">
                                    <span className="pr-4 font-sf-primary">Just In Time (JIT) Policy</span>
                                </div>
                            }
                            accordionSummaryProps={{
                                expandIcon: <Icon icon={Icons.ChevronDown} />
                            }}
                            accordionDetailsProps={{}}>
                            <JitPolicy
                                editedPolicy={editedPolicy}
                                account={null}
                                isBreakGlassDisabled={false}
                                isEditor={true}
                                setEditedPolicy={setEditedPolicy}
                                isActiveOnly={true}
                            />
                        </Accordion>
                    </div>
                </Drawer>
            </div>
        </>
    );
};

export default BulkJit;
